import { getDocs } from "firebase/firestore";
import { businessCollection } from "./firestorePaths";

// Customers and shifts.
//
// Shifts are stored one document per user with an array of sessions, so the
// table's rows have to be flattened out of them.

const str = (v) => (typeof v === "string" ? v.trim() : "");
const num = (v, fallback = 0) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
};

function normalizeCustomer(id, data = {}) {
  const movements = (Array.isArray(data.accountMovements) ? data.accountMovements : []).map(
    (m) => ({
      type: str(m.type),
      method: str(m.method),
      amount: num(m.amount),
      current: num(m.current),
      currency: str(m.currency),
      date: num(m.date),
      staff: str(m.staff),
    })
  );

  const lastMovement = movements.length
    ? movements.reduce((a, b) => (a.date > b.date ? a : b))
    : null;

  // Firestore holds one customerName field; the table renders first/last
  // separately, so split on the first space.
  const name = str(data.customerName);
  const spaceAt = name.indexOf(" ");
  const firstName = spaceAt === -1 ? name : name.slice(0, spaceAt);
  const lastName = spaceAt === -1 ? "" : name.slice(spaceAt + 1);

  return {
    id: str(data.customerId) || id,
    customerId: str(data.customerId) || id,
    name,
    customerName: name,
    firstName,
    lastName,
    email: str(data.customerEmail),
    phone: str(data.customerPhone),
    customerNo: str(data.customerNo),
    customerNumber: str(data.customerNo),
    notes: str(data.customerNotes),
    address: data.customerAddress || null,
    colour: str(data.customerColour),
    balance: num(data.accountBalance),
    accountBalance: num(data.accountBalance),
    outstanding: num(data.customerOutstanding),
    allowDebt: Boolean(data.customerAllowDebt),
    hasBought: Boolean(data.hasBought),
    // Two separate flags exist; treat either as inactive.
    status: data.deactivated || data.active === false ? "inactive" : "active",
    movements,
    lastActivity: lastMovement ? new Date(lastMovement.date).toISOString() : "",
  };
}

/**
 * Customers, with order counts rolled up.
 *
 * The customer document holds no order history, so totalOrders/lastOrder are
 * derived by matching orders on email - the only identifier both sides share.
 */
export async function fetchCustomers(businessId, orders = []) {
  if (!businessId) return [];
  const snap = await getDocs(businessCollection(businessId, "customers"));

  const stats = {};
  orders.forEach((o) => {
    const email = str(o.customer?.email).toLowerCase();
    if (!email) return;
    const entry = stats[email] || { count: 0, last: "" };
    entry.count += 1;
    if (o.createdAt > entry.last) entry.last = o.createdAt;
    stats[email] = entry;
  });

  return snap.docs.map((d) => {
    const customer = normalizeCustomer(d.id, d.data());
    const match = stats[customer.email.toLowerCase()];
    return {
      ...customer,
      totalOrders: match?.count ?? 0,
      lastOrder: match?.last || "",
    };
  });
}

/**
 * Flattens businesses/{id}/shifts (one doc per user, each holding a `shifts`
 * array) into one row per shift session.
 */
export async function fetchShifts(businessId, staff = {}) {
  if (!businessId) return [];
  const snap = await getDocs(businessCollection(businessId, "shifts"));

  const rows = [];
  snap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const userId = str(data.userId) || docSnap.id;
    const sessions = Array.isArray(data.shifts) ? data.shifts : [];

    sessions.forEach((s, index) => {
      const startTime = num(s.startTime);
      const endTime = num(s.endTime);
      const finished = Boolean(s.finished);

      rows.push({
        // Sessions have no id of their own; index keeps rows stable per user.
        id: `${userId}-${index}`,
        userId,
        staffName: staff[userId]?.name || userId,
        role: staff[userId]?.role || "",
        // SHIFT_STATUS keys: open | verified | closed
        status: !finished ? "open" : s.verified ? "verified" : "closed",
        verified: Boolean(s.verified),
        startTime: startTime ? new Date(startTime).toISOString() : "",
        endTime: endTime ? new Date(endTime).toISOString() : "",
        durationMinutes: startTime && endTime ? Math.round((endTime - startTime) / 60000) : 0,
        pauseStart: num(s.pauseStart),
        pauseEnd: num(s.pauseEnd),
        location: str(s.location),
        counter: str(s.counter),
        checkUps: Array.isArray(s.checkUps) ? s.checkUps : [],
      });
    });
  });

  return rows.sort((a, b) => (a.startTime < b.startTime ? 1 : -1));
}
